import {
  faCircleUser,
  faRightToBracket,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Anchor, Button, PasswordInput, Stack, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import axios from "axios";
import L from "leaflet";
import { SECONDARY_COLOR } from "config/constants";
import { useAuth } from "stores/useAuth";
import { useAuthModals } from "hooks/useAuthModals";
import { useEditMode } from "stores/useEditMode";
import { useGenericModals } from "hooks/useGenericModals";
import { useLanguage } from "hooks/useLanguage";
import { useMapContext } from "stores/useMapContext";
import { useTranslation } from "react-i18next";
import { useOptions } from "stores/optionsStore/OptionsContext";
import { AuthAPIErrors } from "types/app";

export const SignInPanel = () => {
  const { signIn } = useAuth();
  const { openSignUpModal, openVerifyEmailModal, openResetPasswordModal } =
    useAuthModals();
  const { updateSaveable, loading, setLoading } = useOptions();
  const { openErrorModal } = useGenericModals();
  const { changeLanguage } = useLanguage();
  const { editMode } = useEditMode();
  const { t } = useTranslation();
  const { map } = useMapContext();
  const form = useForm({
    initialValues: {
      email: "",
      password: "",
    },
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      setLoading(true);

      const { user } = await signIn(form.values.email, form.values.password);
      if (!user) throw Error("no_user");

      // The subscription status and the storage size are loaded by App.handleAuth, which runs on every sign in.
      // The settings come with the sign in response, so they're applied right away.
      const { settings } = user;
      if (settings) {
        // The language also switches i18n. updateSaveable picks only the saveable options
        // out of the settings (signIn already dropped unknown map overlays)
        if (settings.language) changeLanguage(settings.language, false);
        updateSaveable(settings, false);

        if (
          map &&
          settings.viewCenter &&
          settings.autoSaveView &&
          editMode !== "track"
        ) {
          const bounds = L.latLngBounds([settings.viewCenter]);
          map.fitBounds(bounds, { maxZoom: settings.viewZoom, animate: false });
        }
      }
      setLoading(false);
      return null;
    } catch (error) {
      setLoading(false);

      if (axios.isAxiosError<{ err?: AuthAPIErrors; msg?: string }>(error)) {
        const data = error.response?.data;
        if (data?.err === AuthAPIErrors.EMAIL_NOT_VERIFIED)
          return openVerifyEmailModal(
            "error",
            data.msg ?? "",
            form.values.email
          );
        if (data?.err === AuthAPIErrors.NO_ACCOUNT)
          return form.setErrors({ email: data.msg });
        if (data?.err === AuthAPIErrors.INCORRECT_PASSWORD)
          return form.setErrors({ password: data.msg });
        return openErrorModal({ text: data?.msg ?? t("errors.login_failed") });
      }
      return openErrorModal({ text: t("errors.login_failed") });
    }
  };

  return (
    <form onSubmit={(e) => void handleSubmit(e)}>
      <Stack gap={4}>
        <TextInput
          autoComplete="email"
          size="xs"
          placeholder={t("signup_modal.email.label")}
          required
          {...form.getInputProps("email")}
        />
        <PasswordInput
          autoComplete="current-password"
          size="xs"
          placeholder={t("signup_modal.password.label")}
          required
          {...form.getInputProps("password")}
        />
        {/* A button, so it can be reached with the keyboard; the link color has enough contrast in both schemes */}
        <Anchor
          component="button"
          type="button"
          size="xs"
          style={{ alignSelf: "flex-end" }}
          onClick={openResetPasswordModal}
        >
          {t("auth.forgot_password_button_label")}
        </Anchor>
        <Button
          leftSection={<FontAwesomeIcon icon={faRightToBracket} size="lg" />}
          type="submit"
          size="xs"
          loading={loading}
        >
          {t("auth.login_button_label")}
        </Button>
        <Button
          leftSection={<FontAwesomeIcon icon={faCircleUser} size="lg" />}
          size="xs"
          style={{ backgroundColor: SECONDARY_COLOR }}
          onClick={openSignUpModal}
        >
          {t("auth.create_account_label")}
        </Button>
      </Stack>
    </form>
  );
};
